import {
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import { FormConfigData, FormType } from "../../../api/formConfigs";
import { fetchFormConfigs } from "./formConfigsThunk";

export type RequestStatus =
  | "idle"
  | "loading"
  | "success"
  | "failed";

export const formConfigsAdapter = createEntityAdapter<FormConfigData, FormType>({
  selectId: (formConfig) => formConfig.form_type,
});

export interface FormConfigsState {
  formConfigs: EntityState<FormConfigData, FormType>;
  status: RequestStatus;
  error?: string | null;
}

const initialState: FormConfigsState = {
  formConfigs: formConfigsAdapter.getInitialState(),
  status: "idle",
  error: null,
};

const formConfigsSlice = createSlice({
  name: "formConfigs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormConfigs.pending, (state) => {
        state.status = "loading";
        state.error = null;
        formConfigsAdapter.removeAll(state.formConfigs);
      })

      .addCase(fetchFormConfigs.fulfilled, (state, action) => {
        state.status = "success";

        formConfigsAdapter.setAll(
          state.formConfigs,
          action.payload.form_configs,
        );
      })

      .addCase(fetchFormConfigs.rejected, (state, action) => {
        state.status = "failed";

        state.error =
          action.payload ??
          "Failed to fetch form configs";
      });
  },
});

export const formConfigsReducer = formConfigsSlice.reducer;

const formConfigsSelectors =
  formConfigsAdapter.getSelectors<RootState>(
    (state) => state.formConfigs.formConfigs,
  );

export const {
  selectAll: selectAllFormConfigs,
  selectById: selectFormConfigById,
  selectEntities: selectFormConfigEntities,
  selectIds: selectFormConfigIds,
  selectTotal: selectFormConfigCount,
} = formConfigsSelectors;

export const selectFormConfigsStatus = (
  state: RootState,
) => state.formConfigs.status;

export const selectFormConfigsError = (
  state: RootState,
) => state.formConfigs.error;